import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import Input from '../../components/Input.jsx';
import { Settings, MessageCircle, Send, Check, AlertCircle, Hash, Bell } from 'lucide-react';
import { api } from '../../lib/api.js';

const defaultReminderMessage = 'Reminder Booking Today @channel , Akan ke kantor? Abaikan pesan ini , Batal datang? Tolong batalkan booking ya teman-teman agar slot bisa dipakai rekan lain. terima kasih banyak teman-teman.';
const DEFAULT_REMINDER_TIMES = ['08:00'];
const DEFAULT_REMINDER_WEEKDAYS = [1, 2, 3, 4, 5];
const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const parseTimesInput = (val) =>
  String(val || '')
    .split(',')
    .map((t) => t.trim())
    .filter((t) => /^\d{2}:\d{2}$/.test(t));


function AdminIntegrations() {
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [sendingDaily, setSendingDaily] = useState(false);
  const [testingReminderCh, setTestingReminderCh] = useState(false);
  const [savingReminder, setSavingReminder] = useState(false);

  const [slackConfig, setSlackConfig] = useState({
    webhookUrl: '',
    channel: '',
    enabled: false,
  });

  const [reminderConfig, setReminderConfig] = useState({
    channel: '',
    enabled: true,
    message: defaultReminderMessage,
    times: DEFAULT_REMINDER_TIMES,
    weekdays: DEFAULT_REMINDER_WEEKDAYS,
  });
  const [dailyConfig, setDailyConfig] = useState({ enabled: true });

  const [chOverride, setChOverride] = useState('');
  const [reminderTimesInput, setReminderTimesInput] = useState(DEFAULT_REMINDER_TIMES.join(', '));

  useEffect(() => {
    fetchSlackConfig();
    fetchReminderConfig();
    fetchDailyConfig();
  }, []);
  const fetchDailyConfig = async () => {
    try {
      const { data } = await api.get('/admin/settings/slack-daily');
      setDailyConfig({ enabled: data?.enabled !== false });
    } catch {
      // ignore
    }
  };
  const fetchSlackConfig = async () => {
    try {
      const { data } = await api.get('/admin/settings/slack');
      const cfg = {
        webhookUrl: data.webhook_url || '',
        channel: data.channel || '',
        enabled: !!data.enabled,
      };
      setSlackConfig(cfg);
      // default override isi dengan channel webhook (bisa diubah setelah reminder config datang)
      setChOverride((cfg.channel || '').trim());
    } catch {
      console.log('Slack config not found, using defaults');
    }
  };

  const fetchReminderConfig = async () => {
    try {
      const { data } = await api.get('/admin/settings/slack-reminder');
      const rcfg = {
        channel: data.channel || '',
        enabled: data.enabled !== false,
        message: data.message || defaultReminderMessage,
        times: Array.isArray(data.times) && data.times.length ? data.times : DEFAULT_REMINDER_TIMES,
        weekdays: Array.isArray(data.weekdays) && data.weekdays.length ? data.weekdays : DEFAULT_REMINDER_WEEKDAYS,
      };
      setReminderConfig(rcfg);
      setReminderTimesInput((rcfg.times || DEFAULT_REMINDER_TIMES).join(', '));
      // kalau reminder channel ada, prioritas untuk override test
      if ((rcfg.channel || '').trim()) setChOverride(rcfg.channel.trim());
    } catch {
      // ignore
    }
  };

  const saveSlackConfig = async () => {
    setLoading(true);
    try {
      await api.post('/admin/settings/slack', {
        webhook_url: slackConfig.webhookUrl,
        channel: slackConfig.channel,
        enabled: slackConfig.enabled,
      });
      toast.success('Slack configuration saved');
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to save configuration';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const saveReminderConfig = async () => {
    setSavingReminder(true);
    try {
      await api.post('/admin/settings/slack-reminder', {
        channel: reminderConfig.channel,
        enabled: reminderConfig.enabled,
        message: reminderConfig.message,
        times: parseTimesInput(reminderTimesInput),
        weekdays: reminderConfig.weekdays || DEFAULT_REMINDER_WEEKDAYS,
      });
      toast.success('Reminder settings saved');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save reminder settings');
    } finally {
      setSavingReminder(false);
    }
  };
  const updateSlackEnabled = async (nextEnabled) => {
    if (nextEnabled && !slackConfig.webhookUrl.trim()) {
      toast.error('Please enter webhook URL first');
      return;
    }

    const prevEnabled = slackConfig.enabled;
    setSlackConfig((p) => ({ ...p, enabled: nextEnabled }));
    setLoading(true);

    try {
      await api.post('/admin/settings/slack', {
        webhook_url: slackConfig.webhookUrl,
        channel: slackConfig.channel,
        enabled: nextEnabled,
      });
      toast.success(`Slack notifications ${nextEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setSlackConfig((p) => ({ ...p, enabled: prevEnabled }));
      const msg = err.response?.data?.error || 'Failed to update Slack status';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const updateReminderEnabled = async (nextEnabled) => {
    const prevEnabled = reminderConfig.enabled;
    setReminderConfig((p) => ({ ...p, enabled: nextEnabled }));
    setSavingReminder(true);

    try {
      await api.post('/admin/settings/slack-reminder', {
        channel: reminderConfig.channel,
        enabled: nextEnabled,
        message: reminderConfig.message,
        times: parseTimesInput(reminderTimesInput),
        weekdays: reminderConfig.weekdays || DEFAULT_REMINDER_WEEKDAYS,
      });
      toast.success(`Reminders ${nextEnabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      setReminderConfig((p) => ({ ...p, enabled: prevEnabled }));
      toast.error(e.response?.data?.error || 'Failed to update reminder status');
    } finally {
      setSavingReminder(false);
    }
  };

  const toggleWeekday = (day) => {
    setReminderConfig((p) => {
      const set = new Set(p.weekdays || []);
      if (set.has(day)) set.delete(day);
      else set.add(day);
      return { ...p, weekdays: Array.from(set).sort((a, b) => a - b) };
    });
  };

  const testSlackIntegration = async () => {
    if (!slackConfig.webhookUrl) return toast.error('Please enter webhook URL');
    setTesting(true);
    try {
      await api.post('/admin/slack/test', {
        webhook_url: slackConfig.webhookUrl,
        channel: slackConfig.channel,
      });
      toast.success('Test message sent to Slack');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send test message');
    } finally {
      setTesting(false);
    }
  };

  const sendDailyReport = async () => {
    if (!slackConfig.webhookUrl) return toast.error('Please configure Slack webhook URL first');
    setSendingDaily(true);
    try {
      await api.post('/admin/slack/daily-report', {
        webhook_url: slackConfig.webhookUrl,
        channel: slackConfig.channel,
      });
      toast.success('Daily report sent to Slack');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send daily report');
    } finally {
      setSendingDaily(false);
    }
  };

  // Test Reminder (Channel) â€” sesuai server: /admin/slack/reminder/test
  const testReminderChannel = async () => {
    setTestingReminderCh(true);
    try {
      await api.post('/admin/slack/reminder/test', {
        channel: (chOverride || reminderConfig.channel || slackConfig.channel || '').trim(),
      });
      toast.success('Test reminder (channel) dikirim');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send channel reminder');
    } finally {
      setTestingReminderCh(false);
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div>
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center">
            <Settings className="h-8 w-8 text-purple-400 mr-3" />
            Integrations
          </h1>
          <p className="text-gray-400">Connect Spacebook with external services and tools</p>
        </div>
      </motion.div>

      {/* Slack Webhook */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
        <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-600">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="p-3 bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl">
                <MessageCircle className="h-8 w-8 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white mb-1">Slack Integration</h2>
                <p className="text-gray-400 text-sm">
                  Send booking notifications, daily reports, and channel reminders
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <div className={`w-3 h-3 rounded-full ${slackConfig.enabled ? 'bg-green-400' : 'bg-gray-400'}`} />
              <span className="text-gray-400 text-sm">{slackConfig.enabled ? 'Enabled' : 'Disabled'}</span>
            </div>
          </div>

          <div className="space-y-4">
            <Input
              label="Slack Webhook URL"
              value={slackConfig.webhookUrl}
              onChange={(e) => setSlackConfig((p) => ({ ...p, webhookUrl: e.target.value }))}
              placeholder="https://hooks.slack.com/services/XXX/YYY/ZZZ"
              containerClassName="mb-4"
            />

            <Input
              label="Default Slack Channel (optional)"
              value={slackConfig.channel}
              onChange={(e) => setSlackConfig((p) => ({ ...p, channel: e.target.value }))}
              placeholder="#general atau C12345678"
              leftIcon={<Hash className="w-4 h-4 text-gray-400" />}
              containerClassName="mb-4"
              help="Jika webhook mengizinkan override, pesan akan dikirim ke channel ini. Jika kosong, gunakan default dari webhook."
            />

            <div className="flex items-center space-x-3 mb-6">
              <input
                type="checkbox"
                id="slackEnabled"
                checked={slackConfig.enabled}
                onChange={(e) => updateSlackEnabled(e.target.checked)}
                className="w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
              />
              <label htmlFor="slackEnabled" className="text-sm text-gray-300">
                Enable Slack notifications
              </label>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button onClick={saveSlackConfig} loading={loading} className="flex items-center">
                <Check className="h-4 w-4 mr-2" /> Save Configuration
              </Button>

              <Button
                onClick={testSlackIntegration}
                loading={testing}
                variant="secondary"
                className="flex items-center"
                disabled={!slackConfig.webhookUrl || !slackConfig.enabled}
              >
                <Send className="h-4 w-4 mr-2" /> Test Integration
              </Button>

              <Button
                onClick={sendDailyReport}
                loading={sendingDaily}
                variant="outline"
                className="flex items-center"
                disabled={!slackConfig.webhookUrl || !slackConfig.enabled}
              >
                <MessageCircle className="h-4 w-4 mr-2" /> Send Daily Report
              </Button>
            </div>
          </div>

          {/* Reminder Settings (Channel only) */}
          <div className="mt-8 p-4 bg-gray-700/30 rounded-lg border border-gray-600">
            <h3 className="text-white font-medium mb-3 flex items-center">
              <Bell className="h-5 w-5 mr-2 text-purple-300" /> Reminder Settings (Channel)
            </h3>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-3">
                <Input
                  label="Reminder Channel"
                  value={reminderConfig.channel}
                  onChange={(e) => setReminderConfig((p) => ({ ...p, channel: e.target.value }))}
                  placeholder="#booking atau C012ABCDEF"
                  leftIcon={<Hash className="w-4 h-4 text-gray-400" />}
                  help="Jika kosong, sistem akan pakai channel dari Slack Integration (di atas)."
                />

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Reminder Message</label>
                  <textarea
                    rows={4}
                    value={reminderConfig.message}
                    onChange={(e) => setReminderConfig((p) => ({ ...p, message: e.target.value }))}
                    className="block w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200"
                    placeholder="Tulis pesan reminder di sini..."
                  />
                  <p className="mt-2 text-xs text-gray-400">
                    Support Slack mrkdwn. Kosongkan untuk pakai pesan default.
                  </p>
                </div>

                <Input
                  label="Reminder Times (HH:mm)"
                  value={reminderTimesInput}
                  onChange={(e) => setReminderTimesInput(e.target.value)}
                  placeholder="08:00, 12:00, 17:30"
                  help="Pisahkan dengan koma. Format HH:mm (24 jam)."
                />

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Reminder Days</label>
                  <div className="flex flex-wrap gap-2">
                    {WEEKDAY_LABELS.map((label, idx) => {
                      const checked = (reminderConfig.weekdays || DEFAULT_REMINDER_WEEKDAYS).includes(idx);
                      return (
                        <label key={label} className="flex items-center gap-2 text-sm text-gray-300">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleWeekday(idx)}
                            className="w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
                          />
                          {label}
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    id="remEnabled"
                    type="checkbox"
                    checked={reminderConfig.enabled}
                    onChange={(e) => updateReminderEnabled(e.target.checked)}
                    className="w-4 h-4 text-purple-600 bg-gray-700 border-gray-600 rounded focus:ring-purple-500"
                  />
                  <label htmlFor="remEnabled" className="text-gray-300 text-sm">
                    Enable Slack notifications
                  </label>
                </div>

                <div className="flex gap-2">
                  <Button onClick={saveReminderConfig} loading={savingReminder}>
                    <Check className="h-4 w-4 mr-2" /> Save Reminder Settings
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <Input
                  label="Test Reminder Channel Override"
                  value={chOverride}
                  onChange={(e) => setChOverride(e.target.value)}
                  placeholder="#booking atau C012ABCDEF"
                  leftIcon={<Hash className="w-4 h-4 text-gray-400" />}
                  help="Kosongkan untuk gunakan Reminder Channel di atas (atau channel default webhook)."
                />
                <div className="flex gap-2">
                  <Button onClick={testReminderChannel} loading={testingReminderCh} variant="secondary" disabled={!reminderConfig.enabled}>
                    <Bell className="h-4 w-4 mr-2" /> Test Reminder (Channel)
                  </Button>
                </div>
              </div>
            </div>

            <p className="mt-4 text-gray-400 text-sm">
              Jadwal otomatis: sesuai <b>Reminder Times</b> dan <b>Reminder Days</b>. Daily report terkirim otomatis pada{' '}
              <b>12:00, 16:00, 20:00 WIB</b> (Seninâ€“Jumat). Anda juga bisa kirim manual via tombol di atas.
            </p>
          </div>

          {/* Help */}
          <div className="mt-6 p-4 bg-gray-700/20 rounded-lg border border-gray-600">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm">
                <h4 className="font-medium text-white mb-2">Setup:</h4>
                <ol className="text-gray-400 space-y-1 list-decimal list-inside">
                  <li>Isi Slack Webhook URL (dan optional default channel).</li>
                  <li>Atur Reminder Channel & aktifkan reminders.</li>
                  <li>Gunakan tombol <i>Test Integration</i> dan <i>Test Reminder (Channel)</i> untuk uji kirim.</li>
                </ol>
                <p className="mt-3 text-gray-500 text-xs">
                  Catatan: Versi ini tidak memakai DM per user atau CSV mapping. Semua pengingat dikirim ke satu channel.
                </p>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
}

export default AdminIntegrations;















